import { HIGHLIGHT_ALIAS } from '../../lib/muscles'
import { ANTERIOR, POSTERIOR } from './muscleSilhouetteData'
import { TOKENS } from '../ui/dashboard-primitives'
import { muscleFills, type MusclePalette } from './muscleFills'

// Band mode (dashboard body map): fill answers "how strong against your age".
export type MuscleBand = 'low' | 'mid' | 'near' | 'over'
const BAND_FAINT = TOKENS.creamBorder // a region with no reference
const BAND_FILL: Record<MuscleBand, string> = {
  low: TOKENS.terracotta, // terracotta — well below reference
  // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
  mid: 'rgba(245,237,225,0.30)',
  // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
  near: 'rgba(245,237,225,0.52)',
  over: TOKENS.amber, // amber — at or above reference
}

export type { MusclePalette } from './muscleFills'

const alias = (s: string) => HIGHLIGHT_ALIAS[s] ?? s

interface Props {
  side: 'front' | 'back'
  /** Primary/secondary highlight mode (default). */
  primary?: string[]
  secondary?: string[]
  /** Band mode: colour each region by its reference band instead. Takes
   *  precedence over primary/secondary when provided. */
  bands?: Record<string, MuscleBand>
  /** Primary/secondary colour scheme. Defaults to the historical amber pair;
   *  `'two-tone'` gives secondary its own hue (D-11). Ignored in band mode. */
  palette?: MusclePalette
  className?: string
  interactive?: boolean
  hoverSlug?: string | null
  onRegionEnter?: (slug: string) => void
  onRegionLeave?: (slug: string) => void
}

export function MuscleFigure({ side, primary = [], secondary = [], bands, palette = 'amber',
  className, interactive, hoverSlug, onRegionEnter, onRegionLeave }: Props) {
  // Anterior data is authored 0-195, posterior reaches 220; render both at 220
  // so a front/back pair is the same height (and the posterior's feet, clipped
  // at 200, finally show).
  const data = side === 'front' ? ANTERIOR : POSTERIOR
  const bandMode = bands != null
  const P = new Set(primary.map(alias))
  const S = new Set(secondary.map(alias))
  const B: Record<string, MuscleBand> = {}
  if (bands) for (const [slug, band] of Object.entries(bands)) B[alias(slug)] = band

  const fills = muscleFills(palette)
  const fillFor = (slug: string) =>
    bandMode
      ? (B[slug] ? BAND_FILL[B[slug]] : BAND_FAINT)
      : P.has(slug) ? fills.primary : S.has(slug) ? fills.secondary : fills.untargeted
  const trainedFor = (slug: string) => (bandMode ? slug in B : P.has(slug) || S.has(slug))

  return (
    <svg viewBox="0 0 100 220" preserveAspectRatio="xMidYMid meet" className={className} aria-hidden="true">
      {Object.entries(data).map(([slug, polys]) => {
        const trained = trainedFor(slug)
        const fill = fillFor(slug)
        const hot = trained && hoverSlug === slug
        return (
          <g key={slug} data-slug={slug} data-band={bandMode ? (B[slug] ?? 'none') : undefined}
             style={{ pointerEvents: interactive && trained ? 'auto' : 'none', cursor: interactive && trained ? 'default' : undefined }}
             onMouseEnter={interactive && trained ? () => onRegionEnter?.(slug) : undefined}
             onMouseLeave={interactive && trained ? () => onRegionLeave?.(slug) : undefined}>
            {polys.map((points, i) => (
              <polygon key={i} points={points} fill={fill}
                stroke={hot ? TOKENS.amberLight : 'rgba(59,58,65,0.55)'}
                strokeWidth={hot ? 1.6 : 0.4}
                style={{
                  transition: bandMode
                    ? 'fill 700ms cubic-bezier(0.16,1,0.3,1), stroke 140ms cubic-bezier(.23,1,.32,1), stroke-width 140ms cubic-bezier(.23,1,.32,1)'
                    : 'stroke 140ms cubic-bezier(.23,1,.32,1), stroke-width 140ms cubic-bezier(.23,1,.32,1)',
                }} />
            ))}
          </g>
        )
      })}
    </svg>
  )
}
